/**
 * Map cloud/DB chapter variations (TipTap + generatedAt) → desktop ChapterVariation.
 * Prefer TipTap JSON for content/baseContent so bold/italic/underline survive.
 */

import { serializeChapterContent } from "./chapterContent";
import { tryParseStringifiedTipTapDoc } from "./tipTapPlainText";
import type { ChapterVariation, ChapterVariationStatus } from "./models";

function contentToTipTapJson(value: unknown): string {
  if (value == null) return serializeChapterContent("");
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return serializeChapterContent("");
    const parsed = tryParseStringifiedTipTapDoc(trimmed);
    if (parsed) return serializeChapterContent(parsed);
    return serializeChapterContent(trimmed);
  }
  if (typeof value === "object") {
    return serializeChapterContent(value);
  }
  return serializeChapterContent(String(value));
}

export function mapLegacyVariation(
  raw: Record<string, unknown>,
  index: number
): ChapterVariation {
  const id =
    typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : `variation-${index + 1}`;
  const createdAt =
    typeof raw.createdAt === "string" && raw.createdAt.trim()
      ? raw.createdAt
      : typeof raw.generatedAt === "string" && raw.generatedAt.trim()
        ? raw.generatedAt
        : new Date().toISOString();

  let source: ChapterVariation["source"] = "chat";
  if (raw.source === "manual") source = "manual";
  else if (raw.source === "storyCraft" || raw.basedOnStoryCraft === true) source = "storyCraft";

  const status: ChapterVariationStatus =
    raw.status === "applied" || raw.status === "dismissed" ? raw.status : "open";

  const settings =
    raw.settings && typeof raw.settings === "object"
      ? (raw.settings as Record<string, unknown>)
      : null;
  const model =
    typeof raw.model === "string"
      ? raw.model
      : settings && typeof settings.model === "string"
        ? settings.model
        : undefined;

  const noteParts: string[] = [];
  if (typeof raw.note === "string" && raw.note.trim()) noteParts.push(raw.note.trim());
  if (raw.changeReport && typeof raw.changeReport === "object") {
    const report = raw.changeReport as Record<string, unknown>;
    if (typeof report.summary === "string" && report.summary.trim()) {
      noteParts.push(report.summary.trim());
    }
  }

  const tipTapContent = contentToTipTapJson(raw.content);
  const tipTapBase =
    raw.baseContent != null && String(raw.baseContent).trim()
      ? contentToTipTapJson(raw.baseContent)
      : tipTapContent;

  return {
    id,
    createdAt,
    source,
    prompt: typeof raw.prompt === "string" ? raw.prompt : "",
    baseContent: tipTapBase,
    content: tipTapContent,
    model,
    reason:
      typeof raw.reason === "string" && raw.reason.trim() ? raw.reason.trim() : undefined,
    note: noteParts.length ? noteParts.join("\n") : undefined,
    status,
  };
}

/** Prefer remote variations when present; otherwise keep local (avoid drop-on-pull). */
export function mergeChapterVariations(
  local: ChapterVariation[] | undefined,
  remote: ChapterVariation[] | undefined
): ChapterVariation[] {
  const remoteList = Array.isArray(remote) ? remote : [];
  const localList = Array.isArray(local) ? local : [];
  if (remoteList.length === 0) return localList;
  if (localList.length === 0) return remoteList;

  const byId = new Map<string, ChapterVariation>();
  for (const v of remoteList) byId.set(v.id, v);
  for (const v of localList) {
    if (!byId.has(v.id)) byId.set(v.id, v);
  }
  return [...byId.values()].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export function mapLegacyVariations(
  raw: unknown,
  existing?: ChapterVariation[]
): ChapterVariation[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return Array.isArray(existing) ? existing : [];
  }
  const mapped = raw
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item, i) => mapLegacyVariation(item, i));
  return mergeChapterVariations(existing, mapped);
}
